/**
 * Context that keeps a single PostForm instance open across the whole app.
 * Wrap the app root with PostFormProvider; consume with usePostForm() anywhere.
 */
import { createContext, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AnyPost } from '@/api/endpoints/posts'

export type PostFormTab = 'post' | 'event' | 'bazaar' | 'help' | 'action'

// Group posting (Phase B1, unified entry — see
// SPEC_group_post_creation_unified_entry.md): the (+) button always posts
// "in" the group (author = the caller, tagged via groupTagSlug) unless the
// composer's own identity picker is switched to "as the group"
// (postAsGroupSlug, mirror account). `canPostAsGroup` just says whether
// that picker should be offered at all — the actual choice lives as local
// state inside PostForm/EventForm/BazaarForm, not here.
//
// canPostAsGroup also gates the 'action' tab itself (not just the identity
// picker within it) — group_admin/group_editor are the only holders of both
// post_as_group and manage_actions, see spec-action-eot-mvp.md.
export interface PostFormGroupContext {
  slug: string
  name: string
  canPostAsGroup: boolean
  isOpen: boolean
}

interface PostFormContextValue {
  open: boolean
  initialPost: AnyPost | undefined
  initialTab: PostFormTab
  groupContext: PostFormGroupContext | undefined
  openNew: (tab?: PostFormTab, groupContext?: PostFormGroupContext) => void
  openEdit: (post: AnyPost) => void
  close: () => void
}

const PostFormContext = createContext<PostFormContextValue>({
  open: false,
  initialPost: undefined,
  initialTab: 'post',
  groupContext: undefined,
  openNew: () => undefined,
  openEdit: () => undefined,
  close: () => undefined,
})

export function usePostForm(): PostFormContextValue {
  return useContext(PostFormContext)
}

const EDIT_ROUTE_BY_TYPE: Partial<Record<AnyPost['type'], string>> = {
  event: '/posts/new/event',
  offer: '/posts/new/bazaar',
  action: '/posts/new/action',
}

export function PostFormProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [initialPost, setInitialPost] = useState<AnyPost | undefined>(undefined)
  const [initialTab, setInitialTab] = useState<PostFormTab>('post')
  const [groupContext, setGroupContext] = useState<PostFormGroupContext | undefined>(undefined)

  const openNew = (tab: PostFormTab = 'post', ctx?: PostFormGroupContext) => {
    setInitialPost(undefined)
    setInitialTab(tab)
    setGroupContext(ctx)
    setOpen(true)
  }

  // Event/offer/action editing reuses their full creation forms (all their
  // type-specific fields live there, not in this modal) rather than
  // duplicating that UI here — same route as creation, in edit mode via
  // location.state.initialPost. Only post/help stay in this modal.
  const openEdit = (post: AnyPost) => {
    const editRoute = EDIT_ROUTE_BY_TYPE[post.type]
    if (editRoute) {
      navigate(editRoute, { state: { initialPost: post } })
      return
    }
    setInitialPost(post)
    setInitialTab('post')
    setGroupContext(undefined)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    setInitialPost(undefined)
    setGroupContext(undefined)
  }

  return (
    <PostFormContext.Provider value={{ open, initialPost, initialTab, groupContext, openNew, openEdit, close }}>
      {children}
    </PostFormContext.Provider>
  )
}
